// src/components/viewer/exifFormatter.ts
import { CameraExifInfo } from '../../types/exif';
import { MatchingConfidence } from '../../types/domain';

export interface FormattedExposure {
  aperture: string;      // e.g. "ƒ/2.8" or "—"
  shutterSpeed: string;  // e.g. "1/500s" or "2s" or "—"
  iso: string;           // e.g. "ISO 100" or "—"
  focalLength: string;   // e.g. "24 mm" or "—"
  hasData: boolean;
}

export interface FormattedCamera {
  displayName: string;   // e.g. "Sony ILCE-7M4"
  lens?: string;         // e.g. "FE 24-70mm F2.8 GM II"
  hasCamera: boolean;
}

export interface FormattedTimestamp {
  dateFormatted: string; // e.g. "14 ott 2024"
  timeFormatted: string; // e.g. "16:42:15"
  timezoneLabel: string; // e.g. "UTC+1 • Atlantic/Madeira"
}

export interface FormattedCoordinates {
  formattedLat: string;  // e.g. "32.6485° N"
  formattedLon: string;  // e.g. "16.9040° W"
  combined: string;      // e.g. "32.6485° N, 16.9040° W"
  isValid: boolean;
}

export interface ConfidenceMeta {
  label: string;
  badgeClass: string;
  dotClass: string;
  description: string;
}

export interface CategoryStyle {
  pillClass: string;
  borderClass: string;
  dotColor: string;
}

/**
 * Normalizes camera make and model, preventing redundant duplication like "Apple Apple iPhone 15 Pro".
 */
export function formatCamera(exif?: CameraExifInfo | Record<string, any>): FormattedCamera {
  if (!exif) {
    return { displayName: 'Dispositivo non registrato', hasCamera: false };
  }

  const raw = exif as Record<string, any>;
  const make = (raw.make || raw.Make || raw.cameraMake || '').trim();
  const rawModel = (raw.model || raw.Model || raw.cameraModel || raw.camera || '').trim();
  const lens = (raw.lens || raw.LensModel || raw.lensModel || '').trim() || undefined;

  if (!make && !rawModel) {
    return { displayName: 'Dispositivo non registrato', lens, hasCamera: false };
  }

  let displayName = rawModel;
  if (make && rawModel) {
    if (rawModel.toLowerCase().startsWith(make.toLowerCase())) {
      displayName = rawModel;
    } else {
      displayName = `${make} ${rawModel}`;
    }
  } else if (make) {
    displayName = make;
  }

  return {
    displayName,
    lens,
    hasCamera: true,
  };
}

/**
 * Standardizes photography exposure parameters (ƒ/stop, shutter speed fraction, ISO, focal length).
 */
export function formatExposure(exif?: CameraExifInfo | Record<string, any>): FormattedExposure {
  if (!exif) {
    return {
      aperture: '—',
      shutterSpeed: '—',
      iso: '—',
      focalLength: '—',
      hasData: false,
    };
  }

  const raw = exif as Record<string, any>;
  // 1. Aperture
  let aperture = '—';
  const rawAperture = raw.aperture || raw.FNumber;
  if (rawAperture !== undefined && rawAperture !== null) {
    const str = String(rawAperture).trim();
    if (str.startsWith('f/') || str.startsWith('ƒ/')) {
      aperture = `ƒ/${str.slice(2)}`;
    } else if (!isNaN(Number(str))) {
      const num = Number(str);
      const rounded = Math.round((num + 1e-6) * 10) / 10;
      aperture = `ƒ/${rounded.toFixed(1)}`;
    } else {
      aperture = `ƒ/${str}`;
    }
  }

  // 2. Shutter speed
  let shutterSpeed = '—';
  const rawShutter = raw.shutterSpeed || raw.exposureTime || raw.ExposureTime;
  if (rawShutter !== undefined && rawShutter !== null) {
    const str = String(rawShutter).trim();
    if (str.includes('/')) {
      shutterSpeed = str.endsWith('s') ? str : `${str}s`;
    } else {
      const num = parseFloat(str);
      if (!isNaN(num) && num > 0) {
        if (num >= 1) {
          shutterSpeed = `${Number.isInteger(num) ? num : num.toFixed(1)}s`;
        } else {
          shutterSpeed = `1/${Math.round(1 / num)}s`;
        }
      } else {
        shutterSpeed = str;
      }
    }
  }

  // 3. ISO
  let iso = '—';
  const rawIso = raw.iso || raw.ISO;
  if (rawIso !== undefined && rawIso !== null) {
    const isoVal = String(rawIso).replace(/[^0-9]/g, '');
    iso = isoVal ? `ISO ${isoVal}` : `ISO ${rawIso}`;
  }

  // 4. Focal Length
  let focalLength = '—';
  const rawFocal = raw.focalLength || raw.FocalLength;
  if (rawFocal !== undefined && rawFocal !== null) {
    const str = String(rawFocal).trim();
    if (str.toLowerCase().endsWith('mm')) {
      const val = str.slice(0, -2).trim();
      focalLength = `${val} mm`;
    } else if (!isNaN(Number(str))) {
      focalLength = `${Math.round(Number(str))} mm`;
    } else {
      focalLength = `${str} mm`;
    }
  }

  const hasData =
    aperture !== '—' || shutterSpeed !== '—' || iso !== '—' || focalLength !== '—';

  return {
    aperture,
    shutterSpeed,
    iso,
    focalLength,
    hasData,
  };
}

/**
 * Formats capture timestamp with localized timezone information using Intl.DateTimeFormat.
 */
export function formatExifTimestamp(
  timestampUtc: number,
  timezoneId?: string
): FormattedTimestamp {
  if (!timestampUtc || timestampUtc <= 0) {
    return {
      dateFormatted: 'Data non disponibile',
      timeFormatted: '—',
      timezoneLabel: timezoneId || 'UTC',
    };
  }

  const tz = timezoneId || 'UTC';
  const dateObj = new Date(timestampUtc * 1000);

  try {
    const dateFormatter = new Intl.DateTimeFormat('it-IT', {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const timeFormatter = new Intl.DateTimeFormat('it-IT', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const dateFormatted = dateFormatter.format(dateObj);
    const timeFormatted = timeFormatter.format(dateObj);

    // Calculate timezone offset display (e.g. UTC+1)
    let offsetHours = 0;
    try {
      const tzDate = new Date(dateObj.toLocaleString('en-US', { timeZone: tz }));
      const utcDate = new Date(dateObj.toLocaleString('en-US', { timeZone: 'UTC' }));
      offsetHours = Math.round((tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60));
    } catch {
      offsetHours = 0;
    }

    const sign = offsetHours >= 0 ? '+' : '';
    const timezoneLabel = `UTC${sign}${offsetHours} • ${tz}`;

    return {
      dateFormatted,
      timeFormatted,
      timezoneLabel,
    };
  } catch {
    // Fallback if timezone string is invalid
    return {
      dateFormatted: dateObj.toLocaleDateString('it-IT'),
      timeFormatted: dateObj.toLocaleTimeString('it-IT'),
      timezoneLabel: 'UTC',
    };
  }
}

/**
 * Formats GPS coordinates to traditional photography hemisphere format (32.6485° N, 16.9040° W).
 */
export function formatGpsCoordinates(
  lat: number | null | undefined,
  lon: number | null | undefined
): FormattedCoordinates {
  if (
    lat === null ||
    lat === undefined ||
    lon === null ||
    lon === undefined ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    return {
      formattedLat: '—',
      formattedLon: '—',
      combined: 'Posizione non registrata',
      isValid: false,
    };
  }

  const latHemisphere = lat >= 0 ? 'N' : 'S';
  const lonHemisphere = lon >= 0 ? 'E' : 'W';

  const formattedLat = `${Math.abs(lat).toFixed(4)}° ${latHemisphere}`;
  const formattedLon = `${Math.abs(lon).toFixed(4)}° ${lonHemisphere}`;

  return {
    formattedLat,
    formattedLon,
    combined: `${formattedLat}, ${formattedLon}`,
    isValid: true,
  };
}

/**
 * Resolves color coding, labels, and iconography for GPS matching confidence.
 */
export function getConfidenceMeta(confidence: MatchingConfidence): ConfidenceMeta {
  switch (confidence) {
    case 'exact':
      return {
        label: 'GPS Diretto',
        badgeClass: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
        dotClass: 'bg-emerald-400',
        description: 'Coordinate GPS rilevate direttamente dai metadati EXIF della fotocamera',
      };
    case 'interpolated':
      return {
        label: 'Interpolato',
        badgeClass: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
        dotClass: 'bg-sky-400',
        description: 'Posizione calcolata interpolando temporalmente la traccia Polarsteps',
      };
    case 'snap_fallback':
      return {
        label: 'Snap Traccia',
        badgeClass: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
        dotClass: 'bg-amber-400',
        description: 'Posizione associata al punto di traccia Polarsteps temporalmente più vicino',
      };
    case 'step_fallback':
      return {
        label: 'Tappa Polarsteps',
        badgeClass: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
        dotClass: 'bg-purple-400',
        description: 'Posizione associata alle coordinate generali della tappa Polarsteps',
      };
    case 'unmatched':
    default:
      return {
        label: 'Non geolocalizzato',
        badgeClass: 'bg-slate-700/30 text-slate-400 border-slate-600/30',
        dotClass: 'bg-slate-500',
        description: 'Nessun riscontro temporale con la traccia Polarsteps',
      };
  }
}

/**
 * Resolves consistent category styling matching PhotoPopup and CategoryPills.
 */
export function getCategoryStyle(category?: string): CategoryStyle {
  switch (category) {
    case 'Con me':
    case 'Foto con me':
      return {
        pillClass: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
        borderClass: 'border-rose-500/40',
        dotColor: '#f43f5e',
      };
    case 'Panorami':
    case 'Paesaggi':
    case 'Landscape':
      return {
        pillClass: 'bg-sky-500/20 text-sky-300 border-sky-500/30',
        borderClass: 'border-sky-500/40',
        dotColor: '#0ea5e9',
      };
    case 'Dettagli':
    case 'Details':
      return {
        pillClass: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
        borderClass: 'border-emerald-500/40',
        dotColor: '#10b981',
      };
    case 'Cibo':
    case 'Food':
      return {
        pillClass: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        borderClass: 'border-amber-500/40',
        dotColor: '#f59e0b',
      };
    case 'Architettura':
    case 'Architecture':
      return {
        pillClass: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
        borderClass: 'border-indigo-500/40',
        dotColor: '#6366f1',
      };
    default:
      return {
        pillClass: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
        borderClass: 'border-blue-500/40',
        dotColor: '#3b82f6',
      };
  }
}
